import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
// import {UploaderQueueComponent} from './components/uploader-queue/uploader-queue.component';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Logger, NgLoggerModule } from '@nsalaun/ng-logger';
import { DOWNLOAD_BASEURL } from '@pwc-bei/common';
import { I18nModule } from '@pwc-bei/i18n';
import { PwcIconsModule } from '@pwc-bei/icons';
import {
  CRYPTO_PARAMS,
  ErrorInterceptor,
  JwtInterceptor,
  SecurityModule,
} from '@pwc-bei/security';
import { UIModule } from '@pwc-bei/ui';
import { NgWizardConfig, NgWizardModule, THEME } from 'ng-wizard3';
import { CookieService } from 'ngx-cookie-service';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { NgxMaskDirective } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { provideNgxWebstorage, withSessionStorage } from 'ngx-webstorage';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AtecoModule } from './components/ateco/ateco.module';
import { CompileQuestionnaireComponent } from './components/compile-questionnaire/compile-questionnaire.component';
import { CtaRdpComponent } from './components/cta-rdp/cta-rdp/cta-rdp.component';
import { DelegateTableComponent } from './components/delegate-table/delegate-table.component';
import { DownloaderQueueModule } from './components/downloader-queue/downloader-queue.module';
import { EducationSideButtonsComponent } from './components/education-side-buttons/education-side-buttons.component';
import { LabelModule } from './components/label/label.module';
import { FaqFooterComponent } from './components/layout/faq-footer/faq-footer.component';
import { FooterComponent } from './components/layout/footer/footer.component';
import { HeaderComponent } from './components/layout/header/header.component';
import { LayoutComponent } from './components/layout/layout.component';
import { NotificationListComponent } from './components/notifications/components/notification-list/notification-list.component';
import { NotificationsPanelComponent } from './components/notifications/components/notifications-panel/notifications-panel.component';
import { SocietySelectionComponent } from './components/society-selection/society-selection.component';
import { TermsConditionsComponent } from './components/terms-conditions/terms-conditions.component';
import { UpgradePlanModule } from './components/upgrade-plan/upgrade-plan.module';
import { BandiModule } from './pages/bandi/bandi.module';
import { BasePage } from './pages/base-page/base-page';
import { BandiIncentiviModuleComponent } from './pages/bazar/pages/bandi-incentivi-module/bandi-incentivi-module.component';
import { BandiIncentiviComponent } from './pages/bazar/pages/bandi-incentivi/bandi-incentivi.component';
import { BazarComponent } from './pages/bazar/pages/bazar/bazar.component';
import { JourneyComponent } from './pages/bazar/pages/journey/journey.component';
import { QuestionnaireIntroComponent } from './pages/bazar/pages/questionnaire-intro/questionnaire-intro.component';
import { RecommendedServicesComponent } from './pages/bazar/pages/recommended-services/recommended-services.component';
import { RedirectToB2bComponent } from './pages/bazar/pages/redirect-to-b2b/redirect-to-b2b.component';
import { ThematicAreasComponent } from './pages/bazar/pages/thematic-areas/thematic-areas.component';
import { DelegationConfirmedComponent } from './pages/delegation-confirmed/delegation-confirmed.component';
import { DelegateListComponent } from './pages/delegation/delegate-list/delegate-list.component';
import { IncentiviModule } from './pages/incentivi/incentivi.module';
import { PrivacyPolicyTextComponent } from './pages/privacy-policy-text/privacy-policy-text.component';
import { UserManualComponent } from './pages/user-manual/user-manual.component';
import { UserProfileFormComponent } from './pages/user-profile/user-profile-form/user-profile-form.component';
import { UserProfileModule } from './pages/user-profile/user-profile.module';
import { AuthenticationServiceConfig } from './security/authentication-service-config';
import { SamlLoginComponent } from './components/saml-login/pages/saml-login.component';
import { SpecificTermsConditionsComponent } from './components/specific-terms-conditions/specific-terms-conditions.component';

const ngWizardConfig: NgWizardConfig = {
  theme: THEME.default,
};

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    LayoutComponent,
    FooterComponent,
    // UploaderQueueComponent,
    NotificationsPanelComponent,
    NotificationListComponent,
    PrivacyPolicyTextComponent,
    UserProfileFormComponent,
    BasePage,
    CtaRdpComponent,
    FaqFooterComponent,
    DelegationConfirmedComponent,
    DelegateListComponent,
    SocietySelectionComponent,
    DelegateTableComponent,
    TermsConditionsComponent,
    SpecificTermsConditionsComponent,
    BazarComponent,
    RedirectToB2bComponent,
    JourneyComponent,
    ThematicAreasComponent,
    QuestionnaireIntroComponent,
    RecommendedServicesComponent,
    BandiIncentiviComponent,
    CompileQuestionnaireComponent,
    EducationSideButtonsComponent,
    UserManualComponent,
    BandiIncentiviModuleComponent,
    SamlLoginComponent,
  ],
  exports: [TranslateModule],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CommonModule,
    SecurityModule,
    UIModule,
    PwcIconsModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    NgxMaskDirective,
    // LayoutModule,
    ToastrModule.forRoot({
      timeOut: 10000,
    }),
    I18nModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NgLoggerModule.forRoot(environment.logLevel),
    LoggerModule.forRoot({ level: NgxLoggerLevel.ERROR }),
    NgWizardModule.forRoot(ngWizardConfig),
    NgIdleKeepaliveModule.forRoot(),
    DownloaderQueueModule,
    NgxUiLoaderModule.forRoot({
      bgsColor: '#D04A02',
      bgsOpacity: 0.5,
      bgsPosition: 'bottom-right',
      bgsSize: 60,
      bgsType: 'ball-spin-clockwise',
      blur: 1,
      delay: 0,
      fastFadeOut: true,
      fgsColor: '#D04A02',
      fgsPosition: 'center-center',
      fgsSize: 40,
      fgsType: 'cube-grid',
      gap: 24,
      logoPosition: 'center-center',
      logoSize: 120,
      logoUrl: '',
      masterLoaderId: 'master',
      overlayBorderRadius: '0',
      overlayColor: 'rgba(40, 40, 40, 0.8)',
      pbColor: 'red',
      pbDirection: 'ltr',
      pbThickness: 3,
      hasProgressBar: false,
      text: '',
      textColor: '#FFFFFF',
      textPosition: 'center-center',
      maxTime: -1,
      minTime: 300,
    }),
    LabelModule,
    UserProfileModule,
    AtecoModule,
    BandiModule,
    IncentiviModule,
    UpgradePlanModule,
  ],
  providers: [
    provideNgxWebstorage(withSessionStorage()),
    { provide: 'FRAMEWORK_BASEURL', useValue: environment.authUrl },
    {
      provide: 'AUTH_OPTIONS',
      useClass: AuthenticationServiceConfig,
      multi: false,
    },
    { provide: DOWNLOAD_BASEURL, useValue: environment.apiUrl },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: CRYPTO_PARAMS, useValue: environment.encryption },
    CookieService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(
    private readonly router: Router,
    private logger: Logger,
  ) {
    if (environment.enableRoutingTracing) {
      router.events.subscribe(this.logger.debug);
    }
  }
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    '/assets/i18n/', // or whatever path you're using
    '.json',
  );
}
